import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Search, Filter, MapPin, Grid, List, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { KuponCard } from '../components/KuponCard';
import { InviteBusinessModal } from '../components/InviteBusinessModal';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PIN_ICON_16, LEAFLET_ICON_CONFIG, LEAFLET_USER_ICON_CONFIG } from '../assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default icon issue - use custom LokAlGrow pin
delete L.Icon.Default.prototype._getIconUrl;

// Custom LokAlGrow marker icon
const lokalgrowIcon = new L.Icon(LEAFLET_ICON_CONFIG);

// Fallback to default if custom fails
L.Icon.Default.mergeOptions({
  iconRetinaUrl: PIN_ICON_16,
  iconUrl: PIN_ICON_16,
  shadowUrl: null,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
});

export const KuponsPage = () => {
  const { t } = useTranslation();
  const [kupons, setKupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [view, setView] = useState('grid'); // grid or map
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    maxPrice: '',
    maxDistance: '10'
  });

  useEffect(() => {
    getUserLocation();
  }, []);

  useEffect(() => {
    if (userLocation) {
      fetchKupons();
    }
  }, [userLocation, filters]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          // Use default location (Houston)
          setUserLocation({ lat: 29.7604, lng: -95.3698 });
          toast.info('Usando ubicación predeterminada');
        }
      );
    } else {
      setUserLocation({ lat: 29.7604, lng: -95.3698 });
    }
  };

  const fetchKupons = async () => {
    setLoading(true);
    try {
      const params = {};
      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
      }
      if (filters.category) params.category = filters.category;
      if (filters.maxPrice) params.max_price = filters.maxPrice;
      if (filters.maxDistance) params.max_distance = filters.maxDistance;

      const response = await axios.get(`${API}/kupons`, { params });
      const kuponsData = response.data;
      
      // Fetch review stats for each kupon
      const kuponsWithRatings = await Promise.all(
        kuponsData.map(async (kupon) => {
          try {
            const statsResponse = await axios.get(`${API}/reviews/stats/kupon/${kupon.id}`);
            return { ...kupon, ...statsResponse.data };
          } catch (error) {
            return { ...kupon, averageRating: 0, totalReviews: 0 };
          }
        })
      );
      
      setKupons(kuponsWithRatings);
    } catch (error) {
      console.error('Error fetching kupons:', error);
      toast.error('Error al kargar kupones');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-manrope font-bold text-3xl sm:text-4xl mb-2">{t('vip_offers_title')}</h1>
            <p className="text-muted-foreground">{t('vip_offers_subtitle')}</p>
          </div>
          <Button
            onClick={() => setInviteModalOpen(true)}
            className="bg-local-sage hover:bg-local-sage/90 rounded-full"
            data-testid="invite-business-button"
          >
            <Store className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">{t('invite_business')}</span>
            <span className="sm:hidden">{t('invite_short')}</span>
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-2xl p-6 shadow-card mb-8" data-testid="filters-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('category')}</label>
              <Input
                placeholder={t('category_placeholder')}
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="rounded-xl"
                data-testid="category-filter"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('max_price')}</label>
              <Input
                type="number"
                placeholder="50"
                value={filters.maxPrice}
                onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                className="rounded-xl"
                data-testid="price-filter"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('max_distance')}</label>
              <Input
                type="number"
                placeholder="10"
                value={filters.maxDistance}
                onChange={(e) => handleFilterChange('maxDistance', e.target.value)}
                className="rounded-xl"
                data-testid="distance-filter"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={fetchKupons}
                className="w-full bg-local-sage hover:bg-local-sage/90 rounded-xl"
                data-testid="apply-filters-button"
              >
                <Search className="w-5 h-5 mr-2" />
                {t('search')}
              </Button>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">
            {kupons.length} {t('offers_found')}
          </p>
          <div className="flex gap-2">
            <Button
              variant={view === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('grid')}
              className="rounded-full"
              data-testid="grid-view-button"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={view === 'map' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('map')}
              className="rounded-full"
              data-testid="map-view-button"
            >
              <MapPin className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="kupons-grid">
            {kupons.map((kupon) => (
              <KuponCard key={kupon.id} kupon={kupon} />
            ))}
          </div>
        ) : (
          <div className="h-[600px] rounded-2xl overflow-hidden shadow-card" data-testid="kupons-map">
            {userLocation && (
              <MapContainer
                center={[userLocation.lat, userLocation.lng]}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {kupons.map((kupon) => {
                  if (kupon.location) {
                    return (
                      <Marker key={kupon.id} position={[kupon.location.lat, kupon.location.lng]} icon={lokalgrowIcon}>
                        <Popup>
                          <div className="p-2">
                            <h3 className="font-semibold">{kupon.title}</h3>
                            <p className="text-sm text-muted-foreground">{kupon.businessName}</p>
                            <p className="text-lg font-bold text-local-sage mt-2">
                              {kupon.discountedPrice.toFixed(2)}€
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  }
                  return null;
                })}
              </MapContainer>
            )}
          </div>
        )}

        {!loading && kupons.length === 0 && (
          <div className="text-center py-20">
            <MapPin className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('no_offers_found')}</p>
          </div>
        )}
      </div>

      {/* Invite Business Modal */}
      <InviteBusinessModal 
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onInviteSuccess={() => {
          // Refresh user points or show success
        }}
      />
    </div>
  );
};